/**
 * /stats rendered to strings: rows from the RPCs in, cells out. Every formatting decision is
 * here so tests/unit/stats.test.ts can read the page off fixture JSON without a DOM (vitest
 * runs in node with no solid plugin, so a .tsx cannot be imported by a test).
 *
 * Orientation: metrics are rows, labelled by the RPC's own column names so every number traces
 * to a column by name; the 1–8 Sep hand-run baseline is the first number column; days run
 * newest-first to the right. On a 375 px phone the label and the baseline stay put while the
 * days scroll, and "the baseline beside each number" is only possible when the metric is the
 * row. Formatting is by column, never by value: retention_daily's fractions, ratios, depths and
 * milliseconds all arrive as numeric. Nothing here computes a date (a null d1 is the server's
 * word) and nothing re-derives demotion from the rounded bounds (the column is the ruling).
 */
import type { Account } from '../api/account';
import type { BaselineRow, BenchSummary, CardRankRow, DailyRow, LoopRow, RetentionRow, StatsResult, Totals } from '../api/stats';

/** The doors retention is read by; 'make' is the makers' link (/feed?via=make&make=1, Sprint 2). */
export type Via = 'all' | 'pilot' | 'landing' | 'endcard' | 'make';
export const VIAS: readonly Via[] = ['all', 'pilot', 'landing', 'endcard', 'make'];
/** What retention_daily is asked for: null is every door. */
export function viaArg(v: Via): string | null {
  return v === 'all' ? null : v;
}
/** The literal call, as the section heading. */
export function retentionCall(days: number, via: Via): string {
  return `retention_daily(${days}, ${via === 'all' ? 'null' : `'${via}'`})`;
}

export type Gate = 'no-backend' | 'signin' | 'not-admin' | 'admin';
/** Which door to show. The server decides what is readable (42501 on every reader); this only picks the copy. */
export function statsGate(a: Account): Gate {
  if (!a.configured) return 'no-backend';
  if (a.isAdmin) return 'admin';
  if (a.isAccount) return 'not-admin';
  return 'signin'; // anonymous, or a sign-up whose email is pending: both sign in with the admin's password
}

/** One cell: the value; the n it was read over when the RPC gives one; and why there is no value. */
export interface Cell {
  text: string;
  n?: string;
  /** pending: not readable yet (D1 before the next UTC day ends). none: nothing to count. na: the readout did not read it. */
  state?: 'pending' | 'none' | 'na';
}
export interface MetricRow {
  /** The RPC's column name; also the row label. */
  key: string;
  baseline: Cell;
  cells: Cell[];
}
export interface MetricTable {
  days: string[];
  rows: MetricRow[];
  notes: string[];
}

export const DASH = '—';
export const PENDING = '…';
export const NA_CELL: Cell = { text: 'n/a', state: 'na' };
export const NONE_CELL: Cell = { text: DASH, state: 'none' };
export const PENDING_CELL: Cell = { text: PENDING, state: 'pending' };
export const BASELINE_HEAD = '1–8 Sep';
export const BASELINE_HEAD_PER_DAY = '1–8 Sep /day';

export const NOTE_ORDER = 'Newest day first; the first day column is today (UTC), still filling.';
export const NOTE_LEGEND = '… = not readable yet: D1 needs the next UTC day to end. — = nothing to count. n/a = the 1–8 Sep readout did not read it.';
export const NOTE_POPULATION =
  'Baseline: the hand-run readout over 1–8 Sep (tools/sql/retention-readout.sql). Same definitions of held, first visit and D1; not the same population — the readout counted every session (webviews and headless included, internal actors and probes excluded); these rows count first visits from browsers, one door at a time when a filter is on.';
export const NOTE_DAILY_BASELINE =
  'Baseline: 1–8 Sep, hand-run, as an average per day over the readout’s n days (every client, webviews and headless included). Live rows leave out internal actors, probes and headless clients.';
export const NOTE_NO_BASELINE = 'No baseline row yet: the readout has not been written into metric_snapshot.';
export const NOTE_NO_ARRIVALS = 'No arrivals through this door yet.';
export const NOTE_NO_FIRST_VISITS = 'No first visits from browsers in the window.';
export const NOTE_NO_DAYS = 'No days yet.';
export const NOTE_RANK =
  'votes: one network, one vote, per card, per day, capped at 20; a first session, an internal actor and a card’s own uploader never vote. demotion: slots down — only with 20 votes and a hold_ucb a full 0.10 under pool_hold_rate, at most 3 (k_n_min, k_step, k_d_max: migration 0008 lines 55–57). Nothing a card reports about itself is in this table.';

// Formatters, exported for the tests. Fixed decimals so a column lines up.
export function fmtPct(x: number | null): string {
  return x === null ? DASH : `${(x * 100).toFixed(1)}%`;
}
export function fmtDepth(x: number | null): string {
  return x === null ? DASH : Number.isInteger(x) ? String(x) : x.toFixed(1);
}
export function fmtRatio(x: number | null): string {
  return x === null ? DASH : x.toFixed(2);
}
export function fmtMs(x: number | null): string {
  return x === null ? DASH : `${Math.round(x)} ms`;
}
/** '2026-09-14' -> '09-14', as the profile sheet prints days. */
export function fmtDay(day: string): string {
  return day.slice(5);
}

type Kind = 'count' | 'pct' | 'depth' | 'ratio' | 'ms';
function fmt(kind: Kind, v: number): string {
  return kind === 'count' ? String(v) : kind === 'pct' ? fmtPct(v) : kind === 'depth' ? fmtDepth(v) : kind === 'ratio' ? fmtRatio(v) : fmtMs(v);
}
function cell(kind: Kind, v: number | null, n?: number | null): Cell {
  if (v === null) return NONE_CELL;
  return n === undefined || n === null ? { text: fmt(kind, v) } : { text: fmt(kind, v), n: String(n) };
}
function baselineRow(b: StatsResult<BaselineRow[]>, metric: string): BaselineRow | undefined {
  return b.ok ? b.rows.find((r) => r.name === 'baseline' && r.metric === metric) : undefined;
}
function baselineCell(b: StatsResult<BaselineRow[]>, metric: string | undefined, kind: Kind): Cell {
  const row = metric ? baselineRow(b, metric) : undefined;
  if (!row) return NA_CELL;
  if (row.value === null) return NONE_CELL;
  return row.n === null ? { text: fmt(kind, row.value) } : { text: fmt(kind, row.value), n: String(row.n) };
}
/** sessions and first_visit_actors carry n = days (readout block 7, lines 312-320): value/n is a per-day mean. n is read from the row, never hard-coded. */
function perDayCell(row: BaselineRow | undefined): Cell {
  if (!row || row.value === null || !row.n) return NA_CELL;
  return { text: `${(row.value / row.n).toFixed(1)}/day`, n: `${row.n} days` };
}
/** What to say about the baseline read, before the fixed notes: that it failed, that it is not written yet, or nothing. */
function baselineNotes(b: StatsResult<BaselineRow[]>): string[] {
  if (!b.ok) return [`Baseline: ${b.error}`];
  return b.rows.some((r) => r.name === 'baseline') ? [] : [NOTE_NO_BASELINE];
}
function newestFirst<T extends { day: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0));
}

const DAILY_SPECS: Array<{ key: Exclude<keyof DailyRow, 'day'>; baseline?: string }> = [
  { key: 'dau' },
  { key: 'new_actors', baseline: 'first_visit_actors' },
  { key: 'account_dau' },
  { key: 'sessions', baseline: 'sessions' },
];

/** stats_daily(14): dau, new_actors, account_dau, sessions — the four the sprint names. */
export function dailyTable(daily: DailyRow[], baseline: StatsResult<BaselineRow[]>): MetricTable {
  const days = newestFirst(daily);
  return {
    days: days.map((r) => fmtDay(r.day)),
    rows: DAILY_SPECS.map((s) => ({
      key: s.key,
      baseline: s.baseline ? perDayCell(baselineRow(baseline, s.baseline)) : NA_CELL,
      cells: days.map((r) => ({ text: String(r[s.key]) })),
    })),
    notes: [...(days.length ? [] : [NOTE_NO_DAYS]), ...baselineNotes(baseline), NOTE_ORDER, NOTE_DAILY_BASELINE],
  };
}

const RETENTION_SPECS: Array<{ key: Exclude<keyof RetentionRow, 'day'>; kind: Kind; n?: (r: RetentionRow) => number | null; baseline?: string }> = [
  { key: 'first_visits_browser', kind: 'count' },
  { key: 'pos0_hold', kind: 'pct', n: (r) => r.pos0_n, baseline: 'pos0_hold_first_session' },
  { key: 'reached_end', kind: 'pct' },
  { key: 'visits', kind: 'count' },
  { key: 'median_depth', kind: 'depth', baseline: 'median_depth' },
  { key: 'back_taps_per_visit', kind: 'ratio' },
  { key: 'share_taps', kind: 'count' },
  { key: 'likes', kind: 'count' },
  { key: 'idle_share_external', kind: 'pct', baseline: 'idle_share_external' },
  { key: 'idle_share_hosted', kind: 'pct', baseline: 'idle_share_hosted' },
  { key: 'webview_share', kind: 'pct' },
  { key: 'boot_ms_p50', kind: 'ms' },
  { key: 'd1', kind: 'pct', n: (r) => r.d1_n, baseline: 'd1' },
];

/** retention_daily(14, via) beside the baseline's five counterparts. */
export function retentionTable(rows: RetentionRow[], baseline: StatsResult<BaselineRow[]>, via: Via): MetricTable {
  const days = newestFirst(rows);
  const nobody = days.length > 0 && days.every((r) => r.first_visits_browser === 0);
  return {
    days: days.map((r) => fmtDay(r.day)),
    rows: RETENTION_SPECS.map((s) => ({
      key: s.key,
      baseline: baselineCell(baseline, s.baseline, s.kind),
      cells: days.map((r) => {
        // d1 is null two ways: not readable yet (d1_n null: the next UTC day is not complete,
        // 0019:414-420) or nobody to count (d1_n 0). The reader must see which.
        if (s.key === 'd1' && r.d1_n === null) return PENDING_CELL;
        return cell(s.kind, r[s.key], s.n ? s.n(r) : undefined);
      }),
    })),
    notes: [
      ...(nobody ? [via === 'all' ? NOTE_NO_FIRST_VISITS : NOTE_NO_ARRIVALS] : []),
      ...baselineNotes(baseline),
      NOTE_ORDER,
      NOTE_LEGEND,
      NOTE_POPULATION,
    ],
  };
}

/**
 * What retention_daily answered, with the door it was asked for. The table is built from this
 * via, never the button's: a tap changes the button at once and the rows only when the RPC
 * answers, and in between the old door's rows must not be captioned as the new door's.
 */
export interface RetentionAnswer {
  via: Via;
  r: StatsResult<RetentionRow[]>;
}
/** The answer for the door on screen, or null while a door switch is in flight (a Read again keeps the door, so its rows stay up while they refresh). */
export function answerFor(a: RetentionAnswer | undefined, current: Via): RetentionAnswer | null {
  return a && a.via === current ? a : null;
}

/** The seven totals in stats_totals()'s column order and names, so a copied line traces to the RPC (the profile sheet keeps its own words). */
export function totalsLine(t: Totals): Array<{ label: string; value: string }> {
  return [
    { label: 'actors', value: String(t.actors) },
    { label: 'accounts', value: String(t.accounts) },
    { label: 'handles', value: String(t.handles) },
    { label: 'live_cards', value: String(t.live_cards) },
    { label: 'linked_cards', value: String(t.linked_cards) },
    { label: 'comments', value: String(t.comments) },
    { label: 'sessions_all_time', value: String(t.sessions_all_time) },
  ];
}

/** The sprint's four (rank, votes, hold_ucb, demotion), card_id, and the pool the sentence compares against — the RPC's own names. */
export const RANK_HEAD = ['rank', 'card_id', 'votes', 'hold_ucb', 'pool_hold_rate', 'demotion'] as const;
export function rankTable(rows: CardRankRow[]): string[][] {
  return [...rows]
    .sort((a, b) => a.rank - b.rank)
    .map((r) => [String(r.rank), r.card_id, String(r.votes), fmtPct(r.hold_ucb), fmtPct(r.pool_hold_rate), String(r.demotion)]);
}

export const DEEPSTATE_ID = 'deepstate-demo';
/** k_n_min: 20260908150000_deepstate_first.sql:57 (0005:336). Copy only — the demotion column is the ruling. */
export const VOTES_TO_MOVE = 20;
/** Outvoted means demotion > 0 (0005:451-457) — the column, never a client-side recomputation of the rounded bounds. */
export function deepstateLine(rows: CardRankRow[], windowDays: number): string {
  const win = `${windowDays}-day window; the feed orders on 28`;
  const r = rows.find((x) => x.card_id === DEEPSTATE_ID);
  if (!r) return `Deepstate is not in the live feed (${win}).`;
  const where = `hold_ucb ${fmtPct(r.hold_ucb)} against pool_hold_rate ${fmtPct(r.pool_hold_rate)}`;
  if (r.demotion > 0) return `Deepstate: outvoted — down ${r.demotion} ${r.demotion === 1 ? 'slot' : 'slots'} on ${r.votes} votes; ${where} (${win}).`;
  if (r.votes < VOTES_TO_MOVE) return `Deepstate: not outvoted — ${r.votes} of the ${VOTES_TO_MOVE} votes it takes to move any card; ${where} (${win}).`;
  return `Deepstate: not outvoted on ${r.votes} votes — ${where} (${win}).`;
}

/** When the baseline was written, for the caption; null when the snapshot has no baseline row. */
export function baselineTaken(rows: BaselineRow[]): string | null {
  const b = rows.find((r) => r.name === 'baseline');
  return b ? `written ${b.taken_at.slice(0, 10)}` : null;
}

// ---------------------------------------------------------------- the loop (0021) and the bench (0020)

/** The funnel in the order a maker walks it; the row labels are loop_funnel's own column names. */
export const LOOP_KEYS = ['brief_copy', 'make_open', 'upload_actors', 'uploads', 'fit_ok', 'live', 'reached', 'stats_open', 'complete'] as const;
export const NOTE_LOOP =
  'People only: not @rand, the house, probes or headless clients. The total counts each actor once over the window, so it is not the sum of the days. ' +
  'uploads and what follows them are counted on the upload’s day. live = flipped by an admin through card_set_status; reached = 10 sessions by people other than the maker, from 3 networks; ' +
  'complete = an upload that went live and reached them, by a maker who opened their numbers.';
export const NOTE_NO_LOOP = 'No loop yet: nobody outside the house has copied the brief or uploaded in the window.';

/** The heading: the literal calls. */
export function loopCall(days: number): string {
  return `loop_funnel(${days}) · bench_summary()`;
}

/** loop_funnel(days): a metric per row, the window's total in the first number column, then the days newest first. */
export function loopTable(rows: LoopRow[]): MetricTable {
  const total = rows.find((r) => r.day === null);
  const days = newestFirst(rows.filter((r): r is LoopRow & { day: string } => r.day !== null));
  const quiet = !total || LOOP_KEYS.every((k) => total[k] === 0);
  return {
    days: days.map((r) => fmtDay(r.day)),
    rows: LOOP_KEYS.map((k) => ({
      key: k,
      baseline: total ? { text: String(total[k]) } : NONE_CELL,
      cells: days.map((r) => ({ text: String(r[k]) })),
    })),
    notes: [...(quiet ? [NOTE_NO_LOOP] : []), NOTE_ORDER, NOTE_LOOP],
  };
}

/** bench_summary() in its own column names; the cap is reported, never enforced (STRATEGY section 5). */
export function benchLine(b: BenchSummary): Array<{ label: string; value: string }> {
  return [
    { label: 'live', value: String(b.live) },
    { label: 'bench', value: String(b.bench) },
    { label: 'oldest_bench_age_h', value: b.oldest_bench_age_h == null ? DASH : `${b.oldest_bench_age_h} h` },
    { label: 'hidden_7d', value: String(b.hidden_7d) },
    { label: 'cap (reported)', value: String(b.cap) },
  ];
}
